/**
 * How a signed-in surface asks the AI Employee and shows the answer as it is
 * written (plan 03 D18). Shared by the Ask panel (components/ai/AIManager) and
 * the Command Briefing AskBar (components/shell/AskBar).
 *
 * The rules, copied from the ITSM build's behaviour on the PA build's SSE code:
 * - stream first; the answer grows in one bubble;
 * - a failure before any text (including the 409 kill switch, a proxy that
 *   buffers, a bad frame) falls back silently to the whole-answer endpoint;
 * - a failure after text keeps what arrived and offers Retry ("lost the thread");
 * - an abort (panel closed, Escape, a new question) ends quietly, no error.
 *
 * Pure and free of path aliases and domain words, so `npm run test:unit` can
 * run it with Node's type stripping and it can move into the template (CHG-19).
 */
import { StreamError, isAbortError, type StreamHandlers, type StreamStep, type StreamToolCall } from './sse.ts'

export type AskResult =
  /** A full answer. `streamed` is false when it came from the fallback endpoint. */
  | { kind: 'answer'; text: string; tools: StreamToolCall[]; streamed: boolean }
  /** The stream broke after some text: keep `text` on screen and offer Retry. */
  | { kind: 'broken'; text: string }
  /** The caller aborted on purpose: show nothing. */
  | { kind: 'aborted' }

export interface AskOptions {
  /** Opens the stream; resolves with the assembled text (lib/api-client apiStreamPost). */
  stream: (handlers: StreamHandlers, signal: AbortSignal) => Promise<string>
  /** The whole-answer endpoint (POST /api/ai/chat). */
  fallback: (signal: AbortSignal) => Promise<{ text: string; tools?: StreamToolCall[] }>
  signal: AbortSignal
  /** Called with the whole text so far, after each fragment. */
  onText?: (soFar: string) => void
  onStep?: (step: StreamStep) => void
}

/**
 * Asks once. Never rejects for a stream problem; rejects only when the fallback
 * endpoint itself fails (the caller then shows its own short error).
 */
export async function askWithStream(options: AskOptions): Promise<AskResult> {
  const { stream, fallback, signal, onText, onStep } = options
  let soFar = ''
  let tools: StreamToolCall[] = []

  try {
    const full = await stream(
      {
        onText: (fragment) => {
          soFar += fragment
          onText?.(soFar)
        },
        onStep,
        onTools: (more) => {
          tools = tools.concat(more)
        },
      },
      signal
    )
    const text = (full || soFar).trim()
    if (text) return { kind: 'answer', text, tools, streamed: true }
  } catch (error) {
    if (signal.aborted || isAbortError(error)) return { kind: 'aborted' }
    const receivedText = error instanceof StreamError ? error.receivedText : soFar.length > 0
    if (receivedText && soFar.trim()) return { kind: 'broken', text: soFar }
    // Before any text: fall through to the whole-answer endpoint, silently.
  }

  if (signal.aborted) return { kind: 'aborted' }
  try {
    const whole = await fallback(signal)
    return { kind: 'answer', text: whole.text.trim(), tools: whole.tools ?? [], streamed: false }
  } catch (error) {
    if (signal.aborted || isAbortError(error)) return { kind: 'aborted' }
    throw error
  }
}

/**
 * Every step arrives twice, `running` then `done`, with the same label, so the
 * label is the key: a known step is updated in place and keeps its position, a
 * new one is appended. Nothing is removed: the list is a record of what Ava
 * read, not a spinner. A `done` frame without a detail keeps the earlier one.
 */
export function mergeStep(existing: StreamStep[] | undefined, step: StreamStep): StreamStep[] {
  const steps = existing ? [...existing] : []
  const at = steps.findIndex((s) => s.label === step.label)
  if (at === -1) {
    steps.push(step)
    return steps
  }
  steps[at] = { ...steps[at], ...step, detail: step.detail ?? steps[at].detail }
  return steps
}

function isPipeRow(line: string): boolean {
  const trimmed = line.trim()
  return trimmed.startsWith('|')
}

/**
 * Holds back the one block at the end of a half-arrived answer that would render
 * as raw characters until its end arrives (PA build `trimUnfinishedBlocks`):
 * an open code fence, a fence marker cut in two, a table row, and (added here)
 * an unclosed `**` on the last line, so a bold name never flashes as `**Ang`.
 * Nothing is lost: when streaming ends the full text renders.
 */
export function trimUnfinishedBlocks(content: string): string {
  const lines = content.split('\n')

  for (let i = lines.length - 1; i >= 0; i--) {
    const trimmed = lines[i].trim()
    if (/^```\S*\s*$/.test(trimmed) && trimmed !== '```') {
      // An opening fence with no closing one after it.
      return lines.slice(0, i).join('\n')
    }
    if (trimmed === '```') break
  }

  const last = lines.length > 0 ? lines[lines.length - 1] : ''
  if (last.trim().startsWith('`') && /^`{1,2}$/.test(last.trim())) {
    return lines.slice(0, -1).join('\n')
  }
  if (isPipeRow(last)) {
    return lines.slice(0, -1).join('\n')
  }

  const markers = last.split('**').length - 1
  if (markers % 2 === 1) {
    const cut = content.lastIndexOf('**')
    return content.slice(0, cut)
  }
  return content
}
